/* eslint-disable jsx-a11y/alt-text */
import { Button, Form, message } from 'antd';
import { Formik } from 'formik';
import React, { memo, useCallback, useState } from 'react';
import styled from 'styled-components';
import * as yup from 'yup';
import { AuthApi, UserApi } from '../../apis';
import { useSelector } from 'react-redux';
import { delay } from '../../utils';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import { BlueButtonContainer } from '../../screens/home/home';
import { ResendCode } from '../form/pin-input';
import { LockKeyhole } from 'lucide-react';
import { TextInputPassword } from '../form/fields/number-input';
import { PasswordValidator } from '../form/password-validator';
import { NavigationRoutes } from '../../navigation/type';
import { GeneralState } from '../../store/reducer/general';

export const validationSchema = yup.object({
  password: yup
    .string()
    .required('Нууц үг оруулна уу')
    .min(12, 'Нууц үг хамгийн багадаа 12 тэмдэгт байх ёстой')
    .matches(/\d/, 'Ядаж 1 тоо агуулсан байх ёстой')
    .matches(/[A-Z]/, 'Ядаж 1 том үсэг агуулсан байх ёстой')
    .matches(/[^A-Za-z0-9]/, 'Ядаж 1 тусгай тэмдэгт агуулсан байх ёстой'),

  passwordVerify: yup
    .string()
    .required('Нууц үгийг давтан оруулна уу')
    .oneOf([yup.ref('password')], 'Нууц үг таарахгүй байна'),
});

const PasswordForm = memo(() => {
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const { forgot } = useSelector((state: { general: GeneralState }) => state.general);
  const [isPassValid, setIsPassValid] = useState<number>(0);

  const onSubmit = useCallback(
    async (values: { username?: string; password?: string }) => {
      setLoading(true);

      try {
        await AuthApi.password({
          password: values.password,
          username: forgot?.username,
        } as any);

        await delay(300);
        navigate(NavigationRoutes.Login_Screen);
        setLoading(false);
      } catch (error: any) {
        messageApi.open({
          type: 'error',
          content: error?.message,
        });
        setLoading(false);
      }
    },
    [forgot?.username, messageApi, navigate]
  );

  return (
    <>
      {contextHolder}
      <Container>
        <Formik
          initialValues={{ password: undefined, passwordVerify: undefined }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ handleSubmit, handleChange, values, errors, touched, setFieldValue }) => {
            return (
              <Form layout="vertical">
                <div className="card">
                  <div className="inputContainer">
                    <div className="w-full">
                      <img src={require('../../assets/ochun.png')} className="w-[220px]" />
                    </div>
                    <div className="h100" />

                    <div className="font-bold text-[16px] leading-[130%] tracking-[-0.01em] text-[#05041B] flex flex-1 justify-start w-full">
                      Шинэ нууц үг үүсгэх
                    </div>

                    <div className="h-[12px]" />

                    <div className="font-normal text-[14px] leading-[130%] w-full tracking-[-0.01em] text-[#4B4A49]">
                      Та өөрийн шинэ нууц үгийг үүсгэнэ үү
                    </div>

                    <div className="h-[24px]" />

                    <Form.Item
                      className="w-[100%]"
                      name="password"
                      label="Шинэ нууц"
                      required
                      validateStatus={errors?.password && touched?.password ? 'error' : ''}
                      help={errors?.password && touched?.password ? errors?.password : ''}
                    >
                      <TextInputPassword name="password" size="large" placeholder="Password ..." />
                    </Form.Item>

                    <PasswordValidator
                      password={values.password}
                      onChangeProgress={(p) => setIsPassValid(p)}
                    />

                    <div className="h-[15px]" />

                    <Form.Item
                      className="w-[100%]"
                      name="passwordVerify"
                      label="Давтан оруулах"
                      validateStatus={
                        (errors as any)?.passwordVerify && (touched as any)?.passwordVerify
                          ? 'error'
                          : ''
                      }
                      help={
                        (errors as any)?.passwordVerify && (touched as any)?.passwordVerify
                          ? (errors as any)?.passwordVerify
                          : ''
                      }
                    >
                      <TextInputPassword
                        name="passwordVerify"
                        size="large"
                        placeholder="Password ..."
                      />
                    </Form.Item>

                    <div className="flex w-full">
                      <BlueButtonContainer className="w-full">
                        <Button
                          type="primary"
                          size="large"
                          className="w-full"
                          disabled={isPassValid !== 100}
                          onClick={() => handleSubmit()}
                          loading={loading}
                        >
                          Хадгалах
                        </Button>
                      </BlueButtonContainer>
                    </div>
                    <div className="h-[20px]" />
                    <ResendCode
                      onResend={() => {
                        console.log('Resent otp code');
                      }}
                    />
                    <div className="h-[20px]" />
                    <div className="w-full h-[72px] rounded-[8px] bg-[#F5F6FA] opacity-100 flex flex-row justify-center items-center relative pl-[10px]">
                      <span className="text-center font-normal text-[14px] leading-[20px] tracking-[0px] text-[#4A5565] flex flex-row">
                        <LockKeyhole size={18} className="absolute left-[9px]" />
                        Таны аюулгүй байдлын төлөө бид 2 үе шаттай баталгаажуулалт ашигладаг.
                      </span>
                    </div>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Container>
    </>
  );
});

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100vh;
  align-items: center;
  border: solid 1px black;
  justify-content: center;

  .title {
    font-weight: 700;
    font-size: 16px;
  }
  .card {
    border: solid 1px #eceaea;
    width: 400px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    padding: 18px;
  }
  .inputContainer {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  .logo {
    width: 180px;
  }
  .h100 {
    height: 30px;
  }
  .btn {
    width: 100%;
  }
`;

PasswordForm.displayName = 'PasswordForm';

export { PasswordForm };
